// src/pages/DemoDashboard.tsx
import React, { JSX, useEffect, useMemo, useRef, useState } from "react";
import FolderList from "../components/Folder";
import AlbumView from "../components/AlbumView";
import { apiService } from "../services/api";
import type { Folder, FileItem } from "../types/types";
import {
  fetchUserFolders,
  createFolderApi,
  updateFolderApi,
  deleteFolderApi,
  moveFolderApi,
  moveFileApi,
  moveMultipleFilesApi,
  moveAllFilesApi,
} from "../services/api";
import FolderNameModal from "../components/FolderNameModal";
import UploadDemo from "./UpoadDemo";
import { getUploadPhase, hasProcessingUploads } from "../utils/uploadPhase";
import MediaDetailsModal from "../components/MediaDetailsModal";

import { useLocation, useNavigate } from "react-router-dom";
import BottomNav from "../components/BottomTabBar";
import { LogOut } from "lucide-react";

type UploadItem = {
  id: string;
  mediaId?: string;
  originalFilename: string;
  fileSize: string;
  mimeType: string;
  status: string;
  contentHash?: string;
  nftTokenId?: string;
  ownerKey?: string;
  owner_key?: string;
  creatorKey?: string;
  creator_key?: string;
  mediaUrl?: string;
  url?: string;
  createdAt?: string;
  [key: string]: any;
};

const DUMMY_FOLDERS: Folder[] = [
  {
    id: "f_all",
    name: "All files",
    parentId: null,
    createdAt: Date.now() - 1000 * 60 * 60 * 24 * 30,
    files: [],
  },
];

function flattenFolders(folders: Folder[]): Folder[] {
  const out: Folder[] = [];
  for (const f of folders) {
    out.push({ ...f, children: undefined });
    if (f.children) out.push(...flattenFolders(f.children));
  }
  return out;
}

function formatBytes(bytes?: number): string {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))}${sizes[i]}`;
}

// ----------------------------------------------------------------------
// PERFECTED UI SKELETONS (MATCHING THE SCREENSHOTS)
// ----------------------------------------------------------------------

function DashboardSkeleton() {
  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start w-full animate-pulse">
      {/* Left Column - Stats */}
      <div className="w-full lg:w-[302px] shrink-0 flex flex-col gap-4 pt-[100px]">
        <div className="h-[114px] rounded-xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10" />
        <div className="h-[114px] rounded-xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10" />
        <div className="h-[200px] rounded-xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10" />
      </div>
      {/* Right Column - Recent Uploads */}
      <div className="flex-1 min-w-0 flex flex-col w-full">
        <div className="h-4 bg-gray-200 dark:bg-white/10 rounded w-24 mb-1.5" />
        <div className="h-10 bg-gray-200 dark:bg-white/10 rounded w-64 mb-6" />
        <div className="p-5 md:p-6 rounded-xl border border-gray-200 dark:border-white/10 w-full bg-gray-50 dark:bg-transparent">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-[240px] rounded-lg bg-gray-200 dark:bg-white/5 border border-gray-300 dark:border-white/10" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LibrarySkeleton() {
  return (
    <div className="flex flex-col lg:flex-row gap-8 items-start w-full animate-pulse">
      {/* Left Column - Sidebar */}
      <div className="w-full lg:w-[260px] shrink-0 flex flex-col gap-4">
        <div className="h-[48px] bg-gray-200 dark:bg-white/10 rounded-lg border border-gray-300 dark:border-white/10" />
        <div className="h-[48px] bg-gray-100 dark:bg-white/5 rounded-lg border border-gray-300 dark:border-white/10" />
        <div className="h-[400px] bg-gray-100 dark:bg-white/5 rounded-xl border border-gray-300 dark:border-white/10 mt-2" />
      </div>
      {/* Right Column - Content */}
      <div className="flex-1 min-w-0 w-full flex flex-col">
        <div className="h-4 bg-gray-200 dark:bg-white/10 rounded w-32 mb-1.5" />
        <div className="h-10 bg-gray-200 dark:bg-white/10 rounded w-48 mb-6" />
        <div className="h-[600px] bg-gray-100 dark:bg-white/5 rounded-xl border border-gray-300 dark:border-white/10" />
      </div>
    </div>
  );
}

function UploadSkeleton() {
  return (
    <div className="w-full flex flex-col bg-transparent animate-pulse">
      <div className="mb-6 text-left w-full max-w-[686px]">
        <div className="h-10 bg-gray-200 dark:bg-white/10 rounded w-72 mb-2" />
        <div className="h-14 bg-gray-100 dark:bg-white/5 rounded w-full" />
      </div>
      <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 w-full items-start">
        {/* Left Column */}
        <div className="flex-1 flex flex-col w-full max-w-[720px]">
          <div className="h-[340px] w-full rounded-xl bg-gray-100 dark:bg-[#121212] border border-gray-300 dark:border-[#2C2C2C] mb-4" />
          <div className="h-[40px] w-full rounded-lg bg-gray-100 dark:bg-[#121212] border border-gray-300 dark:border-[#2C2C2C]" />
        </div>
        {/* Right Column */}
        <div className="w-full lg:w-[280px] shrink-0">
          <div className="h-8 bg-gray-200 dark:bg-white/10 rounded w-48 mb-5" />
          <div className="space-y-5">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex gap-3 items-start">
                <div className="w-9 h-9 shrink-0 bg-gray-200 dark:bg-white/10 rounded-sm" />
                <div className="flex-1 space-y-2 pt-1">
                  <div className="h-4 bg-gray-200 dark:bg-white/10 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 dark:bg-white/5 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-6 max-w-7xl mx-auto animate-pulse">
      <div className="h-4 bg-gray-200 dark:bg-white/10 rounded w-24 mb-2" />
      {/* User Card */}
      <div className="p-6 sm:p-12 rounded-lg border border-gray-300 dark:border-[#6E6E6E] bg-gray-50 dark:bg-black flex items-center gap-4 sm:gap-6">
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-300 dark:bg-white/20 rounded-full shrink-0" />
        <div className="flex-1 space-y-3">
          <div className="h-10 bg-gray-200 dark:bg-white/10 rounded w-48" />
          <div className="h-5 bg-gray-100 dark:bg-white/5 rounded w-64" />
          <div className="h-5 bg-gray-100 dark:bg-white/5 rounded w-1/3" />
        </div>
      </div>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-[120px] rounded-lg border border-gray-300 dark:border-[#6E6E6E] bg-gray-50 dark:bg-black" />
        ))}
      </div>
      {/* Actions Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-[90px] rounded-lg border border-gray-300 dark:border-[#6E6E6E] bg-gray-50 dark:bg-black" />
        ))}
      </div>
    </div>
  );
}

export default function DemoDashboard(): JSX.Element {
  // Theme persistence with localStorage
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const stored = localStorage.getItem('vastavik-theme');
    if (stored) return stored === 'dark';
    return true; // default dark
  });

  useEffect(() => {
    localStorage.setItem('vastavik-theme', isDarkMode ? 'dark' : 'light');
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  const [folders, setFolders] = useState<Folder[]>(DUMMY_FOLDERS);
  const [inboxFiles, setInboxFiles] = useState<FileItem[]>([]);
  const [detailsFile, setDetailsFile] = useState<FileItem | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [activeTab, setActiveTab] = useState<"dashboard" | "library" | "upload" | "profile">("dashboard");
  const [profile, setProfile] = useState<any>(null);
  const [quota, setQuota] = useState<any>(null);
  const [isLoadingSkeleton, setIsLoadingSkeleton] = useState<boolean>(true);

  const [dashboardVisibleCount, setDashboardVisibleCount] = useState<number>(8);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const dashboardScrollRef = useRef<HTMLDivElement>(null);
const libraryScrollRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const navigate = useNavigate();

const handlePageWheel = (e: React.WheelEvent) => {
    const ref =
      activeTab === "dashboard" ? dashboardScrollRef :
      activeTab === "library" ? libraryScrollRef :
      null;
      
    if (!ref?.current) return;
    
    if (ref.current.scrollHeight > ref.current.clientHeight) {
      ref.current.scrollTop += e.deltaY;
      // Note: React might warn about preventDefault on passive listeners. 
      // If it does, you can safely remove the next line.
      e.preventDefault(); 
    }
  };
useEffect(() => {
    const incomingTab = (location.state as any)?.activeTab;
    
    if (incomingTab) {
      setActiveTab(incomingTab);
    } else if (location.pathname === "/library") {
      setActiveTab("library");
    }
  }, [location.pathname, location.state]);

  useEffect(() => {
    const onSwitch = (e: Event) => {
      const tab = (e as CustomEvent)?.detail?.tab;
      if (tab === "dashboard" || tab === "library" || tab === "upload" || tab === "profile") {
        setActiveTab(tab);
        try { window.scrollTo(0, 0); } catch { /* noop */ }
      }
    };
    window.addEventListener("vastavik:switch-tab", onSwitch);
    return () => window.removeEventListener("vastavik:switch-tab", onSwitch);
  }, []);

  const [isMoving, setIsMoving] = useState<boolean>(false);
  const [movingMessage, setMovingMessage] = useState<string>("Moving… please wait");

  const handleLogout = () => {
    if (typeof apiService?.logout === "function") {
      try {
        apiService.logout().catch(() => {});
      } catch {}
    }

    try {
      localStorage.clear();
    } catch {}
    try {
      sessionStorage.clear();
    } catch {}

    setProfile(null);
    setFolders(DUMMY_FOLDERS);
    setInboxFiles([]);
    setSelectedFolder(null);
    setActiveTab("dashboard");

    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const openFolderId = (location.state as any)?.openFolderId;
    if (!openFolderId) return;

    let cancelled = false;

    const findFolderInList = (list: Folder[] | null | undefined) : Folder | null => {
      if (!list) return null;
      const finder = (items: Folder[]): Folder | null => {
        for (const f of items) {
          if (f.id === openFolderId) return f;
          if (f.children) {
            const found = finder(f.children);
            if (found) return found;
          }
        }
        return null;
      };
      return finder(list);
    };

    (async () => {
      let folder = findFolderInList(folders);
      if (!folder) {
        const freshFolders = await loadFoldersFromServer();
        if (cancelled) return;
        folder = findFolderInList(freshFolders);
      }

      if (folder) {
        setSelectedFolder(folder);
        setActiveTab("library");
        navigate(location.pathname, { replace: true, state: {} });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.state, folders, navigate, location.pathname]);

  const [folderModal, setFolderModal] = useState<{
    mode: "create" | "rename";
    folderId?: string;
    initialValue?: string;
  } | null>(null);

  const PAGE_SIZE = 10;
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);

  const flat = useMemo(() => flattenFolders(folders), [folders]);

  const rootDefault = useMemo(
    () =>
      flat.find((f) => f.name === "All files" && (f.parentId === null || f.parentId === undefined)) ??
      null,
    [flat]
  );

  const totalFiles = flat.reduce((s, f) => s + (f.files?.length || 0), 0) + inboxFiles.length;
  const totalStorageBytes =
    flat.reduce((acc, f) => acc + (f.files?.reduce((a, b) => a + (b.size || 0), 0) || 0), 0) +
    inboxFiles.reduce((a, b) => a + (b.size || 0), 0);

  function mapFilesToFolders(uploadsArray: UploadItem[], foldersData: any[]): Folder[] {
    const creatorKeyToFolder = new Map<string, string>();
    foldersData.forEach((folder) => {
      const creatorKeys = folder.creatorKeys || [];
      creatorKeys.forEach((key: string) => creatorKeyToFolder.set(key, folder.id));
    });

    const filesByFolder = new Map<string, FileItem[]>();
    const unmappedFiles: FileItem[] = [];

    uploadsArray.forEach((upload) => {
      const creatorKey =
        upload.creatorKey ||
        upload.creator_key ||
        upload.ownerKey ||
        upload.owner_key ||
        (upload as any).ownerId ||
        (upload as any).owner_id ||
        (upload as any).uploaderId ||
        (upload as any).uploader_id ||
        (upload as any).userKey ||
        (upload as any).user_key;

      const canonicalId = upload.mediaId || upload.id;

      const fileItem: FileItem = {
        id: canonicalId,
        name: upload.originalFilename,
        displayName: upload.originalFilename,
        url: upload.mediaUrl || upload.url || (upload as any).media_url || "",
        type: upload.mimeType,
        size: Number(upload.fileSize || "0"),
        createdAt: upload.createdAt ? Date.parse(upload.createdAt) : Date.now(),
        nftToken: upload.nftTokenId ?? undefined,
        contentHash: upload.contentHash ?? undefined,
        ownerKey: creatorKey,
        status: (upload as any).status,
        progress: (upload as any).progress,
        isRejected: (upload as any).isRejected,
        isUnderReview: (upload as any).isUnderReview,
        rejectionReason: (upload as any).rejectionReason ?? (upload as any).statusMessage,
        blockchainRegistered: (upload as any).blockchainRegistered,
        meta: upload,
      } as unknown as FileItem;

      if (creatorKey && creatorKeyToFolder.has(creatorKey)) {
        const folderId = creatorKeyToFolder.get(creatorKey)!;
        if (!filesByFolder.has(folderId)) filesByFolder.set(folderId, []);
        filesByFolder.get(folderId)!.push(fileItem);
      } else {
        unmappedFiles.push(fileItem);
      }
    });

    const updatedFolders = foldersData.map((folder) => ({
      ...folder,
      files: filesByFolder.get(folder.id) || [],
      children: folder.children || [],
    }));

    setInboxFiles(unmappedFiles);
    return updatedFolders;
  }

  async function loadFoldersFromServer(): Promise<Folder[]> {
    try {
      const res = await fetchUserFolders();
      if (res.folders && Array.isArray(res.folders)) {
        const uploadsRes = await apiService.getUploadHistory(1, 100);
        const uploadsData =
          uploadsRes?.uploads ?? uploadsRes?.data?.uploads ?? uploadsRes?.data ?? uploadsRes ?? [];
        const uploadsArray: UploadItem[] = Array.isArray(uploadsData) ? uploadsData : [];
        const mappedFolders = mapFilesToFolders(uploadsArray, res.folders);
        setFolders(mappedFolders);

        if (selectedFolder) {
          const updatedSelected = mappedFolders.find((f) => f.id === selectedFolder.id);
          if (updatedSelected) setSelectedFolder(updatedSelected);
        }

        return mappedFolders;
      }
    } catch (err) {
    }
    return [];
  }

  useEffect(() => {
    let mounted = true;
    async function loadAll() {
      try {
        setIsLoadingSkeleton(true);
        const [profileRes, quotaRes] = await Promise.allSettled([
          apiService.getProfile(),
          apiService.getStorageQuota(),
        ]);
        if (mounted && profileRes.status === "fulfilled") setProfile(profileRes.value);
        if (mounted && quotaRes.status === "fulfilled") setQuota(quotaRes.value.storageQuota ?? quotaRes.value);
      } catch (err) {
      }
      await loadFoldersFromServer();
      setTimeout(() => {
        if (mounted) setIsLoadingSkeleton(false);
      }, 250);
    }
    loadAll();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const reload = () => { loadFoldersFromServer().catch(() => {}); };
    const onUploadDone = () => reload();
    const onFocus = () => reload();
    const onVisible = () => { if (document.visibilityState === "visible") reload(); };
    const onStorage = (e: StorageEvent) => { if (e.key === "vastavik:lastUpload") reload(); };
    window.addEventListener("vastavik:upload-completed", onUploadDone);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("vastavik:upload-completed", onUploadDone);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  async function refreshProfileAndQuota() {
    try {
      const [p, q] = await Promise.allSettled([apiService.getProfile(), apiService.getStorageQuota()]);
      if (p.status === "fulfilled") setProfile(p.value);
      if (q.status === "fulfilled") setQuota((q.value as any).storageQuota ?? q.value);
    } catch { /* ignore */ }
  }
  useEffect(() => {
    if (activeTab !== "profile") return;
    refreshProfileAndQuota();
    const onFocus = () => refreshProfileAndQuota();
    const onVisible = () => { if (document.visibilityState === "visible") refreshProfileAndQuota(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [activeTab]);

  useEffect(() => {
    if (inboxFiles.length > displayCount) {
      setDisplayCount(inboxFiles.length);
    }
    if (inboxFiles.length === 0 && displayCount !== PAGE_SIZE) {
      setDisplayCount(PAGE_SIZE);
    }
  }, [inboxFiles.length, displayCount]);

  const findFolderById = (id: string): Folder | null => {
    const walk = (list: Folder[]): Folder | null => {
      for (const f of list) {
        if (f.id === id) return f;
        if (f.children) {
          const r = walk(f.children);
          if (r) return r;
        }
      }
      return null;
    };
    return walk(folders);
  };

  const handleOpenFolder = (folderOrId: Folder | string) => {
    let target: Folder | null = null;
    if (typeof folderOrId === "string") target = findFolderById(folderOrId);
    else target = findFolderById(folderOrId.id) ?? folderOrId;
    if (!target) {
      return;
    }
    setSelectedFolder(target);
  };

  const removeFileFromFolders = (fileId: string) => {
    setFolders((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as Folder[];
      const walk = (list: Folder[]) => {
        for (const f of list) {
          f.files = (f.files || []).filter((fi: FileItem) => fi.id !== fileId);
          if (f.children) walk(f.children);
        }
      }
      walk(next);
      return next;
    });
  };

  const removeFileFromAny = (fileId: string) => {
    setInboxFiles((p) => p.filter((x) => x.id !== fileId));
    removeFileFromFolders(fileId);
  };

  const uploadFilesToFolder = async (folderId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const created = Array.from(files).map((f) => ({
      id: "upl_" + Math.random().toString(36).slice(2, 9),
      name: f.name,
      displayName: f.name,
      url: "",
      type: f.type || "application/octet-stream",
      size: f.size,
      createdAt: Date.now(),
    })) as FileItem[];
    if (folderId === rootDefault?.id) {
      setInboxFiles((p) => [...p, ...created]);
      return;
    }
    setFolders((prev) => prev.map((f) => (f.id === folderId ? { ...f, files: [...(f.files || []), ...created] } : f)));
  };

  const moveFilesBetween = async (
    sourceId: string,
    targetId: string | undefined,
    fileIds: string[]
  ) => {
    if (!Array.isArray(fileIds) || fileIds.length === 0) return;

    setMovingMessage("Moving files… please wait");
    setIsMoving(true);

    const prevFolders = JSON.parse(JSON.stringify(folders)) as Folder[];
    const prevInbox = JSON.parse(JSON.stringify(inboxFiles)) as FileItem[];

    const moved: FileItem[] = [];

    const walkFolders = (list: Folder[]) => {
      for (const f of list) {
        for (const fi of f.files || []) {
          if (fileIds.includes(fi.id)) {
            moved.push(fi);
          }
        }
        if (f.children) walkFolders(f.children);
      }
    };
    walkFolders(folders);

    for (const fi of inboxFiles) {
      if (fileIds.includes(fi.id)) moved.push(fi);
    }

    setFolders((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as Folder[];
      const walk = (list: Folder[]) => {
        for (const f of list) {
          f.files = (f.files || []).filter((fi) => !fileIds.includes(fi.id));
          if (f.children) walk(f.children);
        }
      };
      walk(next);
      return next;
    });

    setInboxFiles((prev) => prev.filter((fi) => !fileIds.includes(fi.id)));

    if (!targetId) {
      setInboxFiles((p) => [...p, ...moved]);
    } else {
      setFolders((prev) =>
        prev.map((f) => (f.id === targetId ? { ...f, files: [...(f.files || []), ...moved] } : f))
      );
    }

    try {
      if (!profile?.email) {
        setFolders(prevFolders);
        setInboxFiles(prevInbox);
        setIsMoving(false);
        return;
      }

      const creatorKeys = moved
        .map((f) =>
          f.ownerKey ??
          (f as any).owner_key ??
          (f as any).creatorKey ??
          (f as any).creator_key ??
          f.meta?.ownerKey ??
          f.meta?.owner_key ??
          f.meta?.creatorKey ??
          f.meta?.creator_key ??
          f.id
        )
        .filter((k): k is string => typeof k === "string" && k.length > 0);

      if (creatorKeys.length === 0) {
        alert("Cannot move files: missing file identifiers");
        setFolders(prevFolders);
        setInboxFiles(prevInbox);
        setIsMoving(false);
        return;
      }

      if (creatorKeys.length === 1) {
        await moveFileApi(creatorKeys[0], targetId ?? null);
      } else {
        await moveMultipleFilesApi(creatorKeys, targetId ?? null);
      }

      await loadFoldersFromServer();
    } catch (err) {
      setFolders(prevFolders);
      setInboxFiles(prevInbox);
      alert("Move failed: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsMoving(false);
    }
  };

  const moveAllFilesHandler = async (sourceId: string, targetId: string | undefined) => {
    const source = flat.find((x) => x.id === sourceId);
    if (!source) return;

    const ids = (source.files || []).map((f) => f.id);
    if (ids.length === 0) return;

    setMovingMessage("Moving ALL files… please wait");
    setIsMoving(true);

    try {
      await moveFilesBetween(sourceId, targetId, ids);

      try {
        await moveAllFilesApi(sourceId, targetId ?? null);
      } catch (err) {
      }
    } finally {
      setIsMoving(false);
    }
  };

  const isRoot = selectedFolder === null;

  const mergedRootFolder: Folder | null = useMemo(() => {
    const allFolderFiles = flat.flatMap(f => f.files || []);
    const allFiles = [...allFolderFiles, ...inboxFiles].sort(
      (a, b) => Number(b.createdAt ?? 0) - Number(a.createdAt ?? 0)
    );
    const paginatedFiles = allFiles.slice(0, displayCount);

    const base = rootDefault ?? {
      id: "f_all",
      name: "All files",
      parentId: null,
      createdAt: Date.now(),
    };
    return {
      ...base,
      files: paginatedFiles,
    };
  }, [flat, inboxFiles, displayCount, rootDefault]);

  const createFolderHandler = async (name: string, parentId?: string | null) => {
    try {
      const res = await createFolderApi(name, parentId ?? null);
      await loadFoldersFromServer();
      return res;
    } catch (err) {
      throw err;
    }
  };

  const renameFolderHandler = async (id: string, newName: string) => {
    try {
      await updateFolderApi(id, { name: newName });
      await loadFoldersFromServer();
    } catch (err) {
    }
  };

  const deleteFolderHandler = async (id: string) => {
    try {
      await deleteFolderApi(id);
      await loadFoldersFromServer();
      if (selectedFolder?.id === id) setSelectedFolder(null);
    } catch (err) {
    }
  };

  const moveFolderHandler = async (id: string, newParentId?: string | null) => {
    try {
      await moveFolderApi(id, newParentId ?? null);
      await loadFoldersFromServer();
    } catch (err) {
    }
  };

  const allRecentFiles = useMemo(() => {
    const allFiles = [...inboxFiles, ...flat.flatMap((f) => f.files || [])];
    return allFiles
      .sort((a, b) => Number(b.createdAt ?? 0) - Number(a.createdAt ?? 0));
  }, [inboxFiles, flat]);

  const anyProcessing = useMemo(
    () => hasProcessingUploads([...inboxFiles, ...flat.flatMap((f) => f.files || [])]),
    [inboxFiles, flat]
  );
  useEffect(() => {
    if (!anyProcessing) return;
    const t = setInterval(() => {
      loadFoldersFromServer().catch(() => {});
    }, 6000);
    return () => clearInterval(t);
  }, [anyProcessing]);

  const percentUsed = quota && quota.quotaBytes ? Math.round((quota.usedBytes / quota.quotaBytes) * 100) : 0;

  const Spinner = ({ className = "h-6 w-6" }: { className?: string }) => (
    <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
      <path d="M22 12a10 10 0 00-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );

  return (
    <div onWheel={handlePageWheel} className="min-h-screen text-black dark:text-white bg-white dark:bg-black transition-colors duration-300">
      
      {/* Top nav — Desktop tabs visible */}
   {/* Top nav — Desktop tabs visible, Mobile shows Hamburger */}
      <nav className="
        flex items-center justify-between
        h-[72px] px-6 md:px-10
        bg-white dark:bg-black
        border-b border-black dark:border-white
        transition-colors duration-300
        sticky top-0 z-50
      ">
<div className="flex-1 flex justify-start">
  <button
  
    onClick={() => {
      window.open("https://vastavik.org/",  "_blank", "noopener,noreferrer");
    }}
    className="font-['Caviar_Dreams'] text-[24px] md:text-[28px] text-black dark:text-white tracking-widest cursor-pointer"
  >
    VASTAVIK
  </button>
</div>

        {/* --- DESKTOP TABS --- */}
        <div className="hidden md:flex flex-1 justify-center items-center gap-8 md:gap-12 h-full">
          {(["dashboard", "library", "upload", "profile"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`relative h-full flex flex-col items-center justify-center font-['Product_Sans'] text-[15px] transition-colors ${
                activeTab === tab
                  ? "text-black dark:text-white font-bold"
                  : "text-gray-500 dark:text-[#A0A0A0] hover:text-black dark:hover:text-white font-normal"
              }`}
            >
              <span className="relative inline-block pb-1">
                <span className="capitalize">{tab}</span>
                {activeTab === tab && (
                  <span className="absolute bottom-0 left-0 w-full h-[2px] bg-black dark:bg-white rounded-t-sm" />
                )}
              </span>
            </button>
          ))}
        </div>

        {/* --- DESKTOP RIGHT CONTROLS --- */}
        <div className="hidden md:flex flex-1 justify-end items-center gap-4">
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            title="Toggle Theme"
            className="text-gray-400 hover:text-black dark:hover:text-white transition-colors flex items-center justify-center"
          >
            {isDarkMode ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
            )}
          </button>
          <div className="hidden sm:block text-[13px] text-gray-700 dark:text-[#E0E0E0] font-['Product_Sans'] truncate max-w-[150px] lg:max-w-[200px]">
            {profile?.email ?? ""}
          </div>
          <button
            onClick={handleLogout}
            title="Log out"
            className="text-[#E57373] hover:opacity-80 transition-opacity flex items-center justify-center ml-2"
          >
            <LogOut size={20} strokeWidth={2} />
          </button>
        </div>

        {/* --- MOBILE HAMBURGER MENU ICON --- */}
        <div className="flex md:hidden flex-1 justify-end items-center">
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
            className="text-black dark:text-white p-2 focus:outline-none"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* --- MOBILE DROPDOWN MENU --- */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-[72px] left-0 w-full bg-white dark:bg-black border-b border-black dark:border-white shadow-xl flex flex-col font-['Product_Sans'] animate-in slide-in-from-top-2 duration-200">
            {/* Tabs List */}
            <div className="flex flex-col py-2 border-b border-gray-200 dark:border-[#2C2C2C]">
              {(["dashboard", "library", "upload", "profile"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`px-6 py-4 text-left text-[15px] capitalize font-medium transition-colors ${
                    activeTab === tab
                      ? "text-black dark:text-white bg-gray-50 dark:bg-white/5 border-l-4 border-black dark:border-white pl-5"
                      : "text-gray-600 dark:text-[#A0A0A0] hover:text-black dark:hover:text-white border-l-4 border-transparent"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Account Controls */}
            <div className="flex flex-col p-6 gap-6">
              <div className="flex items-center justify-between">
                <span className="text-[14px] text-gray-700 dark:text-[#E0E0E0] truncate">
                  {profile?.email ?? "User Email"}
                </span>
                
                {/* Theme Toggle inside mobile menu */}
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="text-gray-500 hover:text-black dark:text-gray-400 dark:hover:text-white transition-colors flex items-center gap-2 text-sm font-medium"
                >
                  {isDarkMode ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                      Light Mode
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
                      Dark Mode
                    </>
                  )}
                </button>
              </div>
              
              {/* Signout inside mobile menu */}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleLogout();
                }}
                className="flex items-center gap-2 text-[#EF4444] text-[15px] font-medium"
              >
                <LogOut size={20} strokeWidth={2} /> Sign out
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Main content container – reduced top padding on mobile only */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 pt-2 sm:pt-6 pb-6">
        
        {/* DASHBOARD TAB */}
        {activeTab === "dashboard" && (isLoadingSkeleton ? <DashboardSkeleton /> : (
           <div   
 className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start lg:items-stretch w-full">
            {/* LEFT COLUMN: STATS CARDS */}
            <div className="w-full lg:w-[302px] shrink-0 flex flex-col gap-4 lg:border-r lg:border-black lg:dark:border-white lg:pr-6">
              <div className="p-6 rounded-xl bg-white dark:bg-black border border-black dark:border-white shadow-sm dark:shadow-none">
                <div className="font-['Product_Sans'] font-bold text-[11px] tracking-widest uppercase text-black dark:text-white mb-6">Total Folders</div>
                <div className="font-['Product_Sans'] text-4xl leading-none font-normal text-black dark:text-white">{flat.length}</div>
              </div>
              <div className="p-6 rounded-xl bg-white dark:bg-black border border-black dark:border-white shadow-sm dark:shadow-none">
                <div className="font-['Product_Sans'] font-bold text-[11px] tracking-widest uppercase text-black dark:text-white mb-6">Total Files</div>
                <div className="font-['Product_Sans'] text-4xl leading-none font-normal text-black dark:text-white">{totalFiles}</div>
              </div>
              <div className="p-6 rounded-xl bg-white dark:bg-black border border-black dark:border-white shadow-sm dark:shadow-none flex flex-col justify-center">
                <div className="flex justify-between items-center mb-5">
                  <div className="font-['Product_Sans'] font-bold text-[11px] tracking-widest uppercase text-black dark:text-white">Data Capacity Used</div>
                  <div className="font-['Product_Sans'] text-[11px] text-gray-500 dark:text-gray-400">{percentUsed}%</div>
                </div>
                <div className="font-['Product_Sans'] text-4xl leading-none font-normal text-black dark:text-white mb-6">
                  {formatBytes(quota?.usedBytes ?? totalStorageBytes)}
                </div>
                <div className="mt-auto">
                  <div className="text-[11px] text-gray-500 dark:text-[#848484] mb-2 font-['Product_Sans']">
                    Quota: {formatBytes(quota?.quotaBytes ?? 0)}
                  </div>
                  <div className="w-full h-1 bg-gray-200 dark:bg-[#333] rounded-full overflow-hidden mb-2">
                    <div
                      className="h-full bg-black dark:bg-white rounded-full transition-all"
                      style={{ width: `${Math.min(100, Math.max(0, percentUsed))}%` }}
                    />
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-[#848484] font-['Product_Sans']">
                    Available: {formatBytes(quota?.availableBytes ?? (quota?.quotaBytes ?? 0) - (quota?.usedBytes ?? 0))}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: RECENT UPLOADS */}
           <div
  ref={dashboardScrollRef}
  className="flex-1 min-w-0 flex flex-col w-full lg:h-[calc(100vh-96px)] lg:min-h-0 lg:overflow-y-auto lg:pr-1 pb-6 [&::-webkit-scrollbar]:hidden"
  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
>
              <div className="text-[12px] text-gray-500 dark:text-[#848484] mb-1.5 font-['Product_Sans'] tracking-wide">U › All files</div>
              <h2 className="font-['Caviar_Dreams'] text-[32px] md:text-[36px] font-bold text-black dark:text-white mb-6 tracking-wide">Recently Uploads</h2>
              <div className="p-5 md:p-6 rounded-xl bg-transparent border border-black dark:border-white w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                  {allRecentFiles.slice(0, dashboardVisibleCount).map((file) => {
                    const phase = getUploadPhase(file);
                    return (
                      <div 
                        key={file.id} 
                        onClick={() => setDetailsFile(file)} 
                        className="flex flex-col cursor-pointer rounded-lg bg-gray-50 dark:bg-[#141414] border border-black dark:border-white overflow-hidden hover:border-gray-400 dark:hover:border-gray-500 transition-colors shadow-sm dark:shadow-none h-full"
                      >
                        <div className="relative w-full h-[140px] bg-gray-200 dark:bg-[#222] flex items-center justify-center overflow-hidden shrink-0 border-b border-gray-200 dark:border-[#333]">
                         {file.url ? (
  file.type?.startsWith("image") ? (
    <img 
      src={file.url} 
      alt={file.displayName ?? file.name} 
      className="object-cover w-full h-full" 
    />
  ) : (
    <div className="text-4xl text-gray-400 dark:text-[#555]">📄</div>
  )
) : (
  <div className="text-4xl text-gray-400 dark:text-[#555]">📄</div>
)}
                          {phase.phase === "processing" && (
                            <span className="absolute top-2.5 left-2.5 flex items-center gap-1.5 rounded px-2 py-1 bg-amber-500/10 border border-amber-500/20 text-[10px] font-bold text-amber-500 font-['Product_Sans'] uppercase tracking-wider">
                              <Spinner className="h-2.5 w-2.5" /> {phase.label}
                            </span>
                          )}
                          {phase.phase === "verifying" && (
                            <span className="absolute top-2.5 left-2.5 flex items-center gap-1.5 rounded px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-bold text-emerald-500 font-['Product_Sans'] uppercase tracking-wider">
                              <Spinner className="h-2.5 w-2.5" /> {phase.label}
                            </span>
                          )}
                          {phase.phase === "failed" && (
                            <span className="absolute top-2.5 left-2.5 flex items-center gap-1.5 rounded px-2 py-1 bg-red-50 dark:bg-[#2A1616] border border-red-500/20 text-[10px] font-bold text-red-500 font-['Product_Sans'] uppercase tracking-wider">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> {phase.label}
                            </span>
                          )}
                          {phase.phase === "processing" && (
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20 dark:bg-white/10">
                              <div className="h-full bg-amber-400 transition-all" style={{ width: `${phase.progress}%` }} />
                            </div>
                          )}
                        </div>
                        <div className="p-4 flex flex-col flex-1">
                          <div className="flex-1 flex flex-col mb-4">
                            <div className="text-[13px] leading-snug font-bold text-black dark:text-white font-['Product_Sans'] mb-1.5 line-clamp-2" title={file.displayName ?? file.name}>
                              {file.displayName ?? file.name}
                            </div>
                            <div className="text-[11px] text-gray-500 dark:text-[#848484] font-['Product_Sans']">
                              {formatBytes(file.size)} • {file.type?.split("/")[1] ?? file.type}
                            </div>
                          </div>
                          <div className="pt-3 border-t border-gray-200 dark:border-[#333] mt-auto">
                            <div className="h-[44px] flex flex-col justify-center">
                              {phase.phase === "done" && (
                                <>
                                  <div className="text-[10px] text-gray-500 dark:text-[#848484] font-['Product_Sans'] mb-1">Vastavik ID:</div>
                                  <div className="bg-gray-200 dark:bg-[#222] text-gray-700 dark:text-[#A0A0A0] text-[10px] px-2.5 py-1.5 rounded font-['Product_Sans'] truncate tracking-wider">
                                    {(file as any).ownerKey ?? "—"}
                                  </div>
                                </>
                              )}
                              {phase.phase === "processing" && (
                                <>
                                  <div className="text-[10px] text-amber-500 font-['Product_Sans'] mb-1">Status:</div>
                                  <div className="bg-amber-500/10 text-amber-500 text-[10px] px-2.5 py-1.5 rounded font-['Product_Sans'] truncate">
                                    Certifying… {phase.progress}%
                                  </div>
                                </>
                              )}
                              {phase.phase === "verifying" && (
                                <>
                                  <div className="text-[10px] text-emerald-500 font-['Product_Sans'] mb-1">Status:</div>
                                  <div className="bg-emerald-500/10 text-emerald-500 text-[10px] px-2.5 py-1.5 rounded font-['Product_Sans'] truncate">
                                    Verifying original…
                                  </div>
                                </>
                              )}
                              {phase.phase === "failed" && (
                                <span className="text-[11px] font-bold text-black dark:text-white uppercase tracking-widest font-['Product_Sans']">
                                  View Details
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {allRecentFiles.length === 0 && (
                    <div className="col-span-full text-center py-12 text-gray-500 dark:text-[#848484] font-['Product_Sans']">
                      No recent uploads
                    </div>
                  )}
                </div>
                {allRecentFiles.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-black dark:border-white flex items-center justify-between">
                    {dashboardVisibleCount < allRecentFiles.length ? (
                      <button
                        onClick={() => setDashboardVisibleCount(prev => Math.min(prev + 8, allRecentFiles.length))}
                        className="px-4 py-2 rounded-md border border-black dark:border-white bg-transparent text-black dark:text-white font-['Product_Sans'] text-sm hover:bg-gray-100 dark:hover:bg-white/5 transition"
                      >
                        View more
                      </button>
                    ) : (
                      <div />
                    )}
                    <div className="text-sm text-gray-500 dark:text-[#848484] font-['Product_Sans']">
                      Showing {Math.min(dashboardVisibleCount, allRecentFiles.length)} of {allRecentFiles.length}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* LIBRARY TAB */}
        {activeTab === "library" && (isLoadingSkeleton ? <div className="w-full max-w-7xl mx-auto"><LibrarySkeleton /></div> : (
          <div    
 className="flex flex-col lg:flex-row gap-8 items-start lg:items-stretch w-full">
            <div className="w-full lg:w-[260px] shrink-0 flex flex-col gap-4 lg:border-r lg:border-black lg:dark:border-white lg:pr-6">
              <button 
                onClick={() => setActiveTab("upload")}
                className="w-full py-3.5 bg-black text-white dark:bg-white dark:text-black font-bold font-['Product_Sans'] text-[14px] rounded-lg flex items-center justify-center gap-2 hover:bg-gray-800 dark:hover:bg-gray-200 transition shadow-sm border border-black dark:border-white"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                Upload
              </button>

              <button 
                onClick={() => setFolderModal({ mode: "create" })}
                className="w-full py-3.5 bg-transparent border border-black dark:border-white text-black dark:text-white font-bold font-['Product_Sans'] text-[14px] rounded-lg flex items-center justify-center gap-2 hover:bg-gray-50 dark:hover:bg-[#111] transition" 
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path><line x1="12" y1="11" x2="12" y2="17"></line><line x1="9" y1="14" x2="15" y2="14"></line></svg>
                Create folder
              </button>

              <div className="w-full rounded-xl border border-black dark:border-white bg-white dark:bg-black p-5 mt-2 shadow-sm dark:shadow-none">
                <h2 className="font-['Caviar_Dreams'] text-3xl font-bold text-black dark:text-white mb-6">Library</h2>

                <button 
                  className={`w-full flex items-center justify-between p-3 rounded-lg mb-6 transition-colors border ${isRoot ? 'bg-gray-100 dark:bg-white text-black border-transparent' : 'bg-transparent border-black dark:border-white text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#181818]'}`}
                  onClick={() => setSelectedFolder(null)}
                >
                  <div className="flex items-center gap-3">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"></rect><path d="M10 4v4"></path><path d="M14 4v4"></path><path d="M2 8h20"></path></svg>
                    <span className="font-['Product_Sans'] text-[13px] font-bold">All files (Inbox)</span>
                  </div>
                  <div className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${isRoot ? 'bg-gray-300 dark:bg-gray-200 text-black' : 'bg-gray-200 dark:bg-[#222] text-gray-600 dark:text-gray-300'}`}>
                    {inboxFiles.length}
                  </div>
                </button>

                <div className="text-[10px] text-gray-500 dark:text-gray-400 font-bold tracking-widest mb-3 uppercase font-['Product_Sans']">Folders</div>
                <div className="space-y-1">
                  {folders.filter((f) => !(f.name === "All files" && f.parentId === null)).map(f => (
                    <button 
                      key={f.id}
                      className={`w-full flex items-center justify-between p-2.5 rounded-lg transition-colors ${selectedFolder?.id === f.id ? 'bg-gray-100 dark:bg-white/10 text-black dark:text-white' : 'text-gray-600 dark:text-[#A0A0A0] hover:bg-gray-50 dark:hover:bg-[#181818] hover:text-black dark:hover:text-white'}`}
                      onClick={() => handleOpenFolder(f)}
                    >
                      <div className="flex items-center gap-3">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
                        <span className="font-['Product_Sans'] text-[13px] font-bold">{f.name}</span>
                      </div>
                      <span className="text-[12px] font-['Product_Sans']">{f.files?.length || 0}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div
  ref={libraryScrollRef}
  className="flex-1 min-w-0 w-full flex flex-col h-[calc(100vh-96px)] min-h-0 overflow-y-auto pr-1 pb-6 [&::-webkit-scrollbar]:hidden"
  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
>
              <div className="text-[12px] text-gray-500 dark:text-[#848484] mb-1.5 font-['Product_Sans'] tracking-wide">
                U › {selectedFolder ? selectedFolder.name : "All files"}
              </div>
              <h2 className="font-['Caviar_Dreams'] text-[36px] font-bold text-black dark:text-white mb-6">
                {selectedFolder ? selectedFolder.name : "All files"}
              </h2>

              <div className="w-full border border-black dark:border-white rounded-xl bg-transparent shadow-sm dark:shadow-none">
                 <AlbumView
                   folder={selectedFolder || mergedRootFolder!}
                   showHeader={false}
                   onBack={() => setSelectedFolder(null)}
                   onAddFiles={(files) => uploadFilesToFolder((selectedFolder || mergedRootFolder!).id, files)}
                   onDeleteFolder={() => deleteFolderHandler((selectedFolder || mergedRootFolder!).id)}
                   onRenameFolder={() =>
                     setFolderModal({
                       mode: "rename",
                       folderId: (selectedFolder || mergedRootFolder!).id,
                       initialValue: (selectedFolder || mergedRootFolder!).name,
                     })
                   }
                   onRemoveFile={(id) => removeFileFromAny(id)}
                   allFolders={flat}
                   onMoveFiles={(s: string, t: string, ids: string[]) => moveFilesBetween(s, t, ids)}
                   onMoveAllFiles={(s: string, t: string) => moveAllFilesHandler(s, t)}
                   onOpenFolder={(id: string) => {
                     const f = flat.find((x) => x.id === id);
                     if (f) handleOpenFolder(f);
                   }}
                   breadcrumb={[]}
                 />
              </div>
            </div>
          </div>
        ))}

        {/* UPLOAD TAB */}
        {activeTab === "upload" && (isLoadingSkeleton ? <UploadSkeleton /> : (
          <div className="w-full h-full bg-transparent overflow-hidden">
            <UploadDemo />
          </div>
        ))}

        {/* PROFILE TAB - fully responsive for mobile */}
        {activeTab === "profile" && (isLoadingSkeleton ? <ProfileSkeleton /> : (
          <div className="space-y-6 max-w-7xl mx-auto">
            {/* Breadcrumb / Sub-header */}
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              U &gt; ALL FILES
            </div>

            {/* User Info Card - responsive padding */}
            <div className="p-6 sm:p-12 rounded-lg border border-black dark:border-[#6E6E6E] bg-white dark:bg-black shadow-sm dark:shadow-none">
              <div className="flex items-center gap-4 sm:gap-6">
                {/* Avatar - smaller on mobile */}
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-black text-white dark:bg-white dark:text-black rounded-full flex items-center justify-center text-2xl sm:text-3xl font-bold shrink-0">
                  {profile?.firstName?.[0]?.toUpperCase() ?? "N"}
                </div>
                <div>
                  <h1
                    className="text-2xl sm:text-[40px] text-black dark:text-white mb-1"
                    style={{
                      fontFamily: "'Caviar Dreams', sans-serif",
                      fontWeight: 700,
                      lineHeight: "1.1",
                      letterSpacing: "-0.32px",
                    }}
                  >
                    Profile
                  </h1>
                  <p
                    className="text-sm sm:text-base text-gray-500 dark:text-[#B2B2B2] mb-2 sm:mb-3"
                    style={{
                      fontFamily: "'Product Sans', sans-serif",
                      fontWeight: 300,
                      lineHeight: "1.4",
                    }}
                  >
                    Your Vastavik account overview.
                  </p>
                  <div className="text-sm flex flex-wrap items-center gap-y-1">
                    <span
                      className="text-base sm:text-[18px] text-black dark:text-white"
                      style={{
                        fontFamily: "'Product Sans', sans-serif",
                        fontWeight: 500,
                        lineHeight: "1.4",
                      }}
                    >
                      {profile?.email ?? "nikita@adroitx.net"}
                    </span>
                    <span className="text-gray-400 mx-2">•</span>
                    <span className="text-green-600 dark:text-green-500 font-medium">Signed in</span>
                    <span className="text-gray-400 mx-2">•</span>
                    <span className="text-gray-600 dark:text-gray-400">Tier: {profile?.tier ?? "FREE"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Storage Stats Cards - responsive */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div className="p-4 sm:p-6 rounded-lg border border-black dark:border-[#6E6E6E] bg-white dark:bg-black shadow-sm dark:shadow-none">
                <div
                  className="text-[10px] sm:text-xs text-gray-500 dark:text-[#B2B2B2] mb-2 uppercase tracking-wider"
                  style={{
                    fontFamily: "'Product Sans', sans-serif",
                    fontWeight: 300,
                    letterSpacing: "1.2px",
                  }}
                >
                  DATA CAPACITY USED
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-black dark:text-white">
                  {formatBytes(quota?.usedBytes) ?? "0 B"}
                </div>
              </div>

              <div className="p-4 sm:p-6 rounded-lg border border-black dark:border-[#6E6E6E] bg-white dark:bg-black shadow-sm dark:shadow-none">
                <div
                  className="text-[10px] sm:text-xs text-gray-500 dark:text-[#B2B2B2] mb-2 uppercase tracking-wider"
                  style={{
                    fontFamily: "'Product Sans', sans-serif",
                    fontWeight: 300,
                    letterSpacing: "1.2px",
                  }}
                >
                  QUOTA
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-black dark:text-white">
                  {formatBytes(quota?.quotaBytes) ?? "5GB"}
                </div>
              </div>

              <div className="p-4 sm:p-6 rounded-lg border border-black dark:border-[#6E6E6E] bg-white dark:bg-black shadow-sm dark:shadow-none flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div
                      className="text-[10px] sm:text-xs text-gray-500 dark:text-[#B2B2B2] uppercase tracking-wider"
                      style={{
                        fontFamily: "'Product Sans', sans-serif",
                        fontWeight: 300,
                        letterSpacing: "1.2px",
                      }}
                    >
                      AVAILABLE
                    </div>
                    <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium">
                      {quota?.utilizationPercentage ?? 0}% used
                    </div>
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-black dark:text-white mb-3 sm:mb-4">
                    {formatBytes(quota?.availableBytes) ?? "5GB"}
                  </div>
                </div>
                <div className="w-full h-1 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-black dark:bg-white rounded-full transition-all"
                    style={{ width: `${Math.min(100, quota?.utilizationPercentage ?? 0)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Actions Grid - responsive padding */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <button
                onClick={() => navigate("/storage")}
                className="flex items-center justify-between p-4 sm:p-6 rounded-lg border border-black dark:border-[#6E6E6E] bg-white dark:bg-black hover:bg-gray-50 dark:hover:bg-white/5 transition text-left group shadow-sm dark:shadow-none"
              >
                <div>
                  <span className="block text-base sm:text-lg text-black dark:text-white font-semibold mb-0.5 sm:mb-1">Data Capacity</span>
                  <span className="block text-xs sm:text-sm text-gray-500 dark:text-gray-400">Manage your plan &amp; data capacity</span>
                </div>
                <span className="text-gray-400 text-lg sm:text-xl font-light group-hover:text-black dark:group-hover:text-white transition-colors">›</span>
              </button>

              <button
                onClick={() => navigate("/pricing")}
                className="flex items-center justify-between p-4 sm:p-6 rounded-lg border border-black dark:border-[#6E6E6E] bg-white dark:bg-black hover:bg-gray-50 dark:hover:bg-white/5 transition text-left group shadow-sm dark:shadow-none"
              >
                <div>
                  <span className="block text-base sm:text-lg text-black dark:text-white font-semibold mb-0.5 sm:mb-1">Subscription &amp; Plans</span>
                  <span className="block text-xs sm:text-sm text-gray-500 dark:text-gray-400">Upgrade or change your plan</span>
                </div>
                <span className="text-gray-400 text-lg sm:text-xl font-light group-hover:text-black dark:group-hover:text-white transition-colors">›</span>
              </button>

              <button
                onClick={() => navigate("/profile")}
                className="flex items-center justify-between p-4 sm:p-6 rounded-lg border border-black dark:border-[#6E6E6E] bg-white dark:bg-black hover:bg-gray-50 dark:hover:bg-white/5 transition text-left group shadow-sm dark:shadow-none"
              >
                <div>
                  <span className="block text-base sm:text-lg text-black dark:text-white font-semibold mb-0.5 sm:mb-1">Account Settings</span>
                  <span className="block text-xs sm:text-sm text-gray-500 dark:text-gray-400">Profile, password &amp; more</span>
                </div>
                <span className="text-gray-400 text-lg sm:text-xl font-light group-hover:text-black dark:group-hover:text-white transition-colors">›</span>
              </button>

              <button
                onClick={() => navigate("/help")}
                className="flex items-center justify-between p-4 sm:p-6 rounded-lg border border-black dark:border-[#6E6E6E] bg-white dark:bg-black hover:bg-gray-50 dark:hover:bg-white/5 transition text-left group shadow-sm dark:shadow-none"
              >
                <div>
                  <span className="block text-base sm:text-lg text-black dark:text-white font-semibold mb-0.5 sm:mb-1">Help &amp; Support</span>
                  <span className="block text-xs sm:text-sm text-gray-500 dark:text-gray-400">Get help or contact us</span>
                </div>
                <span className="text-gray-400 text-lg sm:text-xl font-light group-hover:text-black dark:group-hover:text-white transition-colors">›</span>
              </button>
            </div>
          </div>
        ))}
      </div>

{/* MOBILE FIXED BOTTOM NAV – now switches tabs without navigating */}
      {/* <BottomNav
        active={activeTab}
        onNavigate={(tab) => {
          setActiveTab(tab as any);
        }}
      /> */}

      <FolderNameModal
        open={!!folderModal}
        title={folderModal?.mode === "create" ? "Create folder" : "Rename folder"}
        initialValue={folderModal?.initialValue}
        confirmText={folderModal?.mode === "create" ? "Create" : "Rename"}
        onClose={() => setFolderModal(null)}
        onConfirm={async (name) => {
          try {
            if (folderModal?.mode === "create") {
              await createFolderHandler(name);
            }
            if (folderModal?.mode === "rename" && folderModal.folderId) {
              await renameFolderHandler(folderModal.folderId, name);
            }
            setFolderModal(null);
          } catch (err) {
            console.error("Folder operation failed", err);
          }
        }}
      />

      <MediaDetailsModal file={detailsFile} onClose={() => setDetailsFile(null)} />

      {isMoving && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
          <div className="bg-white dark:bg-[#141414] border border-gray-200 dark:border-white/10 rounded-2xl p-6 flex items-center gap-4 shadow-2xl">
            <Spinner />
            <div className="text-sm font-medium text-black dark:text-white">{movingMessage}</div>
          </div>
        </div>
      )}
    </div>
  );
}